/**
 * ListingsTopBar - Mobile top bar for listings browse page
 *
 * Diamond Architecture: Matches Rails Listings::Index::MobileTopBarComponent
 * Layout: [Back] [Search Trigger] [Filters]
 *
 * The CENTRE cell is the shared `SearchForm` — the same component the home
 * hero renders. It was a hand-styled `UnstyledButton` carrying its own copy of
 * the search field's geometry and an `onSearchClick` prop the host had to
 * remember to wire (its first shipped version was wired to a comment); then it
 * was an `EnhancedLink` to `/listings/{mode}`, i.e. to THIS PAGE — so the bar's
 * only search control, on the only viewport that has this bar, was a self-link
 * that reloaded the hub and dropped the reader's filters with it. It is now a
 * native `<form method="get">` that carries those filters as hidden inputs and
 * enhances into the full-screen overlay, and it owns that overlay itself, so
 * neither the geometry nor the wiring nor the query is this file's to get wrong.
 *
 * Mobile only - hidden on desktop where navbar is visible.
 *
 * The back control is the shared `BackLink` (§3.7). It was a REAL ANCHOR to
 * the locale home already (LET-83 — it used to be a `Button onClick` calling
 * `router.push(routes.home())`, a destination that existed only in JS); what
 * it was NOT is a back. It always went home, whatever the reader had come
 * from. `BackLink` pops the app's own history when there is any and falls back
 * to the declared parent when there is not, so the cold-start destination here
 * is unchanged and the warm one becomes a real back.
 *
 * ★ The parent is DECLARED, like every other one. It used to be passed — a
 * `routes.home()` literal right here — with the argument that `canvas` is "the
 * one kind that renders its OWN top bar, so the manifest's `back:` column has
 * nothing to say about it", and that webroutesgen refused the row that would
 * have said it. Both halves were true and together they made this the one back
 * destination in the app that no manifest declared and no gate could see. The
 * chrome table's `mobileHeader` column now says WHO draws the control (`back` =
 * the shell's band, `page` = this bar) rather than WHETHER one exists, so a
 * `canvas` owes a `back:` like any other surface and the shell resolves it —
 * loader override, `internalPath` refusal and all — before handing it down.
 *
 * The RIGHT cell is the shared `ListingsFiltersControl` (§3.2) — the same
 * component the desktop navbar renders. It carried a bare `Button` whose
 * `open` state, badge count and modal all lived in `ListingsClient`, so this
 * bar was the ONLY place the filters could be reached: the navbar is `infra`
 * and may not import a feature. The control owns its own disclosure and reads
 * its badge from the URL, which is what lets one component serve both
 * placements — and what makes "two badges for one URL" unrepresentable.
 */
import { BackLink } from '@/shared/components/BackLink';
import { ListingsFiltersControl } from '@/shared/components/listings/filters/ListingsFiltersControl';
import { useListingsCanvasScope } from '@/shared/components/listings/filters/use-listings-browse-scope';
import { SearchForm } from '@/shared/components/search';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { usePageBackTarget } from '@/shared/providers/PageBackTargetProvider';
import { cn } from '@letsgethome/ui/lib/utils';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';

import type { ListingsSurface } from './listings-surface';

interface ListingsTopBarProps {
  /**
   * WHERE the canvas this bar belongs to lives, which decides two things this
   * bar may not decide for itself (#6915 / N1).
   *
   * `canvas`: the page IS the viewport and draws its own top band — the chrome
   * table's `mobileHeader: 'page'` row — so the bar is `fixed` at the top edge
   * OVER the map, and the back control is this bar's to draw.
   *
   * `inline`: the canvas is a block in a scrolling document under a row whose
   * `mobileHeader` is `'back'`, i.e. the SHELL already draws a band with a back
   * control in it. So the bar sits in flow at the top of its own block and
   * draws NO back control: two would be §2.1's duplicate exactly, and
   * `page-chrome.spec.ts` counts every visible back control a reader could
   * recognise, however it was drawn.
   */
  surface: ListingsSurface;
  /** Additional className */
  className?: string;
}

/**
 * The back cell, split out so the hook that resolves the target is CALLED only
 * where the control is drawn.
 *
 * `usePageBackTarget` THROWS when a page's chrome resolved no target — the right
 * behaviour for the bar that owns the control, and a crash for a bar that draws
 * none. A conditional hook is unspellable; a conditional component is the same
 * thing said legally.
 */
function ListingsTopBarBackCell() {
  const backTo = usePageBackTarget();
  return <BackLink backTo={backTo} />;
}

export function ListingsTopBar({ surface, className }: ListingsTopBarProps) {
  const t = useTranslations('listings');
  const ownsBand = surface === 'canvas';
  // The browse mode is the ROUTE's, not a prop (§2.9/§2.20). The search form's
  // GET target is `/listings/{mode}` and the overlay it opens keeps a
  // selection on that mode, so a bar that did not know it would send a reader
  // browsing `buy` into `rent` — which is exactly why it arrived as a REQUIRED
  // prop from `ListingsClient`, `urlModeFor(mode)`-converted at the call site.
  // It is now read from the matched canvas's `browseScope`, the same
  // derivation the desktop navbar beside it reads, so the two placements of
  // the same control cannot be handed two different modes.
  const { urlMode } = useListingsCanvasScope();

  return (
    <div
      data-slot="listings-top-bar"
      className={cn(
        ownsBand ? 'fixed top-0 inset-x-0 md:hidden' : 'relative shrink-0 md:hidden',
        // ★ THE BAND HAS A DECLARED HEIGHT, and it is the mobile top band's
        // ONE height (`--mobile-header-height`, the `mobile-header-height` role of
        // `manifests/design-tokens.yaml`) — the same variable `MobileTopBar` is sized from and
        // the same one `PageShell` subtracts from a fixed-viewport `main` below
        // `md`. It had none: the height was whatever the 44 px control row plus
        // `py-2` came to (~60 px), knowable by measuring the rendered DOM and
        // by nothing else. That mattered the moment something had to CLEAR the
        // bar — the results drawer at its `full` rail is `h-dvh bottom-0`
        // beneath a `fixed top-0` bar on a higher layer, so the result heading
        // and the first card sat under it. `MobileSheet`'s inset reads this
        // variable; an undeclared height would have made that inset a second
        // guess at a number nothing stated.
        'h-[var(--mobile-header-height)]',
        // The layer is the BAND's, and an in-flow bar is not on a layer at all:
        // it sits above nothing, so stacking it would be a claim about a
        // z-order it does not participate in.
        ownsBand && Z_LAYERS.navbar.className,
        'bg-background/95 backdrop-blur-sm border-b border-border',
        // `pointer-events-none` lets a reader pan the MAP through the gaps
        // between the three cells of a bar that floats over it. An in-flow bar
        // floats over nothing, and the property would only make its own
        // background inert.
        ownsBand && 'pointer-events-none',
        className
      )}
    >
      {/* `h-full` inside the declared band: the 44 px controls CENTRE in the
          bar's height rather than defining it. */}
      <div
        className={cn(
          'grid h-full items-center gap-2 px-2',
          ownsBand ? 'grid-cols-[44px_1fr_44px]' : 'grid-cols-[1fr_44px]'
        )}
      >
        {/* LEFT: the shared back control — only where this bar owns it. */}
        {ownsBand && (
          <div className="pointer-events-auto">
            <ListingsTopBarBackCell />
          </div>
        )}

        {/* CENTER: the shared search field. The active query is not passed —
            the field reads it off the URL through the listings-filter codec, so
            there is one copy of it and the no-JS submit carries the same one. */}
        <div className="pointer-events-auto min-w-0">
          <SearchForm mode={urlMode} placeholder={t('searchLocation')} variant="navbar" />
        </div>

        {/* RIGHT: the SHARED filters control — trigger, badge and modal in one
            component, the same one the desktop navbar's centre slot renders
            (`ListingsBrowseControls`). It was a bare `Button` here with its
            count and its modal held by `ListingsClient`, which is why the
            desktop navbar — `infra`, forbidden to import a feature — could not
            have one at all (obs. 2). Both placements derive the badge from the
            URL, so they agree by construction rather than by a prop. */}
        <div className="pointer-events-auto flex justify-end">
          <ListingsFiltersControl />
        </div>
      </div>
    </div>
  );
}
